import React, { useEffect, useMemo, useState, forwardRef } from "react";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRange, RangeKeyDict } from "react-date-range";
import "./DateRangeWithFlexible.css";
import { addDays, addMonths, format } from "date-fns";
import useOutsideAlerter from "@/hooks/client/useOutsideAlerter";
import {
  cn,
  getDateFormat,
  lgBreakPoint,
  mdBreakPoint,
  screenInnerWidth,
} from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";
import { sub } from "date-fns";
import Calendar from "@/components/ui/svgComponents/Calendar";
import { buildFlexibleMonthOptions } from "./month-options";

export type DateRangeWithFlexibleData = {
  type: "date" | "flexible";
  date?: {
    startDateStr: string;
    endDateStr: string;
  };
  flexible?: {
    stayLength: "Nights" | "Weekend" | "Week";
    selectedMonth: Date;
    nights: number;
  };
};

const minNights = 1;
const maxNights = 10;

type Props = {
  values?: DateRangeWithFlexibleData;
  className?: string;
  labelClassName?: string;
  labelTextClassName?: string;
  inputWrapperClassName?: string;
  disableSameDateSelection?: boolean;
  searchContainerWidth?: number;
  leftOffset?: number;
  byPassLeftOffset?: boolean;
  onChange?:
    | ((data: DateRangeWithFlexibleData, triggerSearch: boolean) => void)
    | React.Dispatch<React.SetStateAction<DateRangeWithFlexibleData>>;
  isWhiteBg?: boolean;
  renderContent?: () => React.ReactNode;
  renderEmptyState?: boolean;
  contentClassName?: string;
  onFocusedChange?: (isFocused: boolean) => void;
  disableFlexibleSelection?: boolean;
  singleMonth?: boolean;
  singleDate?: boolean;
  dropdownTopOffset?: number;
  renderFooter?: () => React.ReactNode;
  hideIcon?: boolean;
  /** Floor for selectable dates. Anything strictly before this is disabled in the picker. */
  minDate?: Date;
  /** Number of starting months offered in the flexible-date picker. */
  flexibleMonthCount?: number;
};

const DateRangeWithFlexible = forwardRef<HTMLDivElement, Props>(
  (
    {
      values: _values,
      className,
      labelClassName,
      labelTextClassName,
      inputWrapperClassName,
      onChange,
      disableSameDateSelection,
      searchContainerWidth,
      renderEmptyState,
      leftOffset,
      byPassLeftOffset,
      isWhiteBg,
      renderContent,
      contentClassName,
      onFocusedChange,
      disableFlexibleSelection,
      singleMonth,
      singleDate,
      dropdownTopOffset,
      renderFooter,
      hideIcon,
      minDate,
      flexibleMonthCount = 6,
    },
    ref
  ) => {
    const [values, setValues] = useState<DateRangeWithFlexibleData | undefined>(
      _values
    );
    const [isFocused, setIsFocused] = useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [isFlexibleSelected, setIsFlexibleSelected] = useState(
      values?.type === "flexible" && !disableFlexibleSelection
    );
    const availableMonths = useMemo(
      () => buildFlexibleMonthOptions(new Date(), flexibleMonthCount),
      [flexibleMonthCount]
    );

    const stayLength = values?.flexible?.stayLength ?? "Weekend";
    const selectedMonth = values?.flexible?.selectedMonth ?? new Date();
    const nights = values?.flexible?.nights ?? 1;

    const resetFlexible = (reset: "date" | "flexible") => {
      setValues({
        type: reset === "date" ? "date" : "flexible",
        ...(reset === "date" ? { date: undefined } : {}),
        flexible:
          reset === "flexible"
            ? {
                stayLength: "Weekend",
                selectedMonth: availableMonths[0] ?? new Date(),
                nights: 1,
              }
            : undefined,
      });
    };

    useEffect(() => {
      onFocusedChange?.(isFocused);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);

    useEffect(() => {
      if (_values) {
        setValues(_values);
        setIsFlexibleSelected(
          _values.type === "flexible" && !disableFlexibleSelection
        );
      }
    }, [_values, disableFlexibleSelection]);

    useEffect(() => {
      if (isFocused === false && values) {
        if (
          values.type === "date" &&
          disableSameDateSelection &&
          values.date?.startDateStr !== undefined &&
          values.date?.startDateStr === values.date?.endDateStr
        ) {
          const startDateStr = values.date?.startDateStr as string;
          const endDate = addDays(getDateFormat(startDateStr), 1);
          const endDateStr = format(endDate, "yyyy-MM-dd");
          onChange?.(
            {
              type: "date",
              date: { startDateStr, endDateStr },
            },
            false
          );
        } else {
          onChange?.(values, false);
        }
      }
    }, [isFocused, values, disableSameDateSelection, onChange]);

    useOutsideAlerter(containerRef, () => {
      setIsFocused(false);
    });

    const onChangeDates: (rangesByKey: RangeKeyDict) => void = (ranges) => {
      if (ranges.selection.startDate && ranges.selection.endDate) {
        const startDateStr = format(
          ranges.selection.startDate as Date,
          "yyyy-MM-dd"
        );
        if (singleDate) {
          const newValues: DateRangeWithFlexibleData = {
            type: "date",
            date: { startDateStr, endDateStr: startDateStr },
          };
          setValues(newValues);
          setIsFocused(false);
          onChange?.(newValues, false);
          return;
        }
        setValues({
          type: "date",
          date: {
            startDateStr,
            endDateStr: format(ranges.selection.endDate as Date, "yyyy-MM-dd"),
          },
        });
        return;
      }
    };
    const monthsToDisplay = singleMonth
      ? 1
      : screenInnerWidth > mdBreakPoint
        ? 2
        : 1;
    const isLessThanLg = screenInnerWidth <= lgBreakPoint;

    return (
      <div className={cn("relative", className)} ref={containerRef}>
        {renderEmptyState ? (
          <div
            className={cn(
              "flex h-full min-h-[56px] w-full cursor-pointer items-center gap-2 pl-3",
              labelClassName
            )}
            onClick={() => setIsFocused((r) => !r)}
            ref={ref}
          >
            {!hideIcon && (
              <Calendar
                className={cn("w-5 text-primary", labelTextClassName)}
              />
            )}
            <span
              className={cn(
                "text-sm font-normal tracking-normal text-muted-foreground",
                labelTextClassName
              )}
            >
              Check in - Check out
            </span>
          </div>
        ) : renderContent ? (
          <div
            className={contentClassName}
            onClick={() => setIsFocused((r) => !r)}
            ref={ref}
          >
            {renderContent()}
          </div>
        ) : (
          <div className="flex h-full w-full flex-1 flex-row rounded-xl">
            <div className={cn("w-full", inputWrapperClassName)}>
              <div
                className={cn(
                  "flex h-full cursor-pointer items-center gap-2 pl-3",
                  labelClassName
                )}
                onClick={() => setIsFocused((r) => !r)}
                ref={ref}
              >
                {!hideIcon && (
                  <Calendar
                    className={cn("w-5 text-primary", labelTextClassName)}
                  />
                )}
                <span
                  className={cn(
                    "text-sm font-light text-primary",
                    labelTextClassName
                  )}
                >
                  {getFlexibleDateLabel(values as DateRangeWithFlexibleData)}
                </span>
              </div>
            </div>
          </div>
        )}
        {isFocused && (
          <div
            className={cn(
              "absolute z-50 w-full",
              !dropdownTopOffset && "top-[68px] lg:top-[80px]"
            )}
            style={{
              ...(dropdownTopOffset != null ? { top: dropdownTopOffset } : {}),
              width: searchContainerWidth,
              left: byPassLeftOffset
                ? (leftOffset ?? 0) * -1
                : isLessThanLg
                  ? 0
                  : (leftOffset ?? 0) * -1,
            }}
          >
            <div
              className={cn(
                "flex min-w-[300px] flex-col items-center justify-center rounded-lg border bg-popover lg:min-w-[500px] lg:max-w-full",
                isWhiteBg
                  ? "border-pure-black/15 bg-pure-white"
                  : "border-pure-white/15"
              )}
            >
              {!disableFlexibleSelection && (
                <div className="mt-6 flex items-center justify-center gap-1 rounded-full border p-1.5 backdrop-blur-sm">
                  <div
                    onClick={() => {
                      setIsFlexibleSelected(false);
                      resetFlexible("date");
                    }}
                    className={cn(
                      "flex cursor-pointer items-center justify-center gap-2.5 rounded-full px-4 py-2",
                      !isFlexibleSelected &&
                        (isWhiteBg ? "bg-tier-gold" : "bg-muted")
                    )}
                  >
                    <div
                      className={cn(
                        "text-sm font-semibold leading-snug text-primary",
                        isWhiteBg && "text-pure-black"
                      )}
                    >
                      Select Dates
                    </div>
                  </div>
                  <div
                    onClick={() => {
                      setIsFlexibleSelected(true);
                      resetFlexible("flexible");
                    }}
                    className={cn(
                      "flex cursor-pointer items-center justify-center gap-2.5 rounded-full px-4 py-2",
                      isFlexibleSelected &&
                        (isWhiteBg ? "bg-tier-gold" : "bg-muted")
                    )}
                  >
                    <div
                      className={cn(
                        "text-sm font-semibold leading-snug text-primary",
                        isWhiteBg && "text-pure-black"
                      )}
                    >
                      I&apos;m Flexible
                    </div>
                  </div>
                </div>
              )}
              {isFlexibleSelected ? (
                <div className="mb-6 w-full">
                  <div
                    className={cn(
                      "mt-3 flex items-center justify-center text-center text-xl font-normal leading-7 text-foreground lg:mt-6",
                      isWhiteBg && "text-pure-black"
                    )}
                  >
                    Stay Length
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-3 lg:mt-4">
                    <Button
                      type="button"
                      variant={
                        stayLength === "Nights" ? "pill-2-selected" : "pill-2"
                      }
                      className={
                        isWhiteBg
                          ? stayLength === "Nights"
                            ? "border-tier-gold bg-tier-gold text-pure-black"
                            : "border-pure-black/20 bg-transparent text-pure-black"
                          : ""
                      }
                      onClick={() => {
                        setValues((values) => ({
                          ...((values ?? {}) as DateRangeWithFlexibleData),
                          flexible: {
                            stayLength: "Nights",
                            selectedMonth:
                              values?.flexible?.selectedMonth ??
                              availableMonths[0] ??
                              new Date(),
                            nights: values?.flexible?.nights ?? 1,
                          },
                        }));
                      }}
                    >
                      Nights
                    </Button>
                    <Button
                      type="button"
                      variant={
                        stayLength === "Weekend" ? "pill-2-selected" : "pill-2"
                      }
                      className={
                        isWhiteBg
                          ? stayLength === "Weekend"
                            ? "border-tier-gold bg-tier-gold text-pure-black"
                            : "border-pure-black/20 bg-transparent text-pure-black"
                          : ""
                      }
                      onClick={() => {
                        setValues((values) => ({
                          ...((values ?? {}) as DateRangeWithFlexibleData),
                          flexible: {
                            stayLength: "Weekend",
                            selectedMonth:
                              values?.flexible?.selectedMonth ??
                              availableMonths[0] ??
                              new Date(),
                            nights: values?.flexible?.nights ?? 1,
                          },
                        }));
                      }}
                    >
                      Weekend
                    </Button>
                  </div>
                  <div
                    className={cn(
                      "mt-3 flex items-center justify-center text-center text-xl font-normal leading-7 text-foreground lg:mt-6",
                      isWhiteBg && "text-pure-black"
                    )}
                  >
                    {stayLength === "Nights"
                      ? "Number of Nights"
                      : `Starting in ${format(selectedMonth ?? new Date(), "MMMM")}`}
                  </div>
                  <div>
                    {stayLength === "Nights" ? (
                      <div className="mt-2 flex select-none items-center justify-center gap-3 lg:mt-4">
                        <div
                          className={cn(
                            "flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border",
                            nights <= minNights &&
                              "cursor-not-allowed opacity-50"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            nights > minNights &&
                              setValues((values) => ({
                                // ...((values ?? {}) as DateRangeWithFlexibleData),
                                type: "flexible",
                                flexible: {
                                  stayLength:
                                    values?.flexible?.stayLength ?? "Nights",
                                  selectedMonth:
                                    values?.flexible?.selectedMonth ??
                                    availableMonths[0] ??
                                    new Date(),
                                  nights: nights - 1,
                                },
                              }));
                          }}
                        >
                          <Minus
                            className={cn(
                              "h-5 w-5",
                              isWhiteBg ? "text-pure-black" : "text-primary"
                            )}
                          />
                        </div>
                        <span
                          className={cn(
                            isWhiteBg ? "text-pure-black" : "text-primary"
                          )}
                        >
                          {nights}
                        </span>
                        <div
                          className={cn(
                            "flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border",
                            nights >= maxNights &&
                              "cursor-not-allowed opacity-50"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            nights < maxNights &&
                              setValues((values) => ({
                                type: "flexible",
                                flexible: {
                                  stayLength:
                                    values?.flexible?.stayLength ?? "Nights",
                                  selectedMonth:
                                    values?.flexible?.selectedMonth ??
                                    availableMonths[0] ??
                                    new Date(),
                                  nights: nights + 1,
                                },
                              }));
                          }}
                        >
                          <Plus
                            className={cn(
                              "h-5 w-5",
                              isWhiteBg ? "text-pure-black" : "text-primary"
                            )}
                          />
                        </div>
                      </div>
                    ) : null}
                    {stayLength === "Nights" ? (
                      <div
                        className={cn(
                          "mt-3 flex items-center justify-center text-center text-xl font-normal leading-7 text-foreground lg:mt-6",
                          isWhiteBg && "text-pure-black"
                        )}
                      >
                        Starting in{" "}
                        {format(selectedMonth ?? new Date(), "MMMM")}
                      </div>
                    ) : null}
                    <div className="mx-auto mt-2 flex max-w-[500px] flex-wrap items-center justify-center gap-3 lg:mt-4">
                      {availableMonths.map((month, index) => {
                        return (
                          <Button
                            type="button"
                            variant={
                              format(selectedMonth, "MM-yyyy") ===
                              format(month, "MM-yyyy")
                                ? "pill-2-selected"
                                : "pill-2"
                            }
                            className={
                              isWhiteBg
                                ? format(selectedMonth, "MM-yyyy") ===
                                  format(month, "MM-yyyy")
                                  ? "border-tier-gold bg-tier-gold text-pure-black"
                                  : "border-pure-black/20 bg-transparent text-pure-black"
                                : ""
                            }
                            key={index}
                            onClick={() =>
                              setValues((values) => ({
                                type: "flexible",
                                flexible: {
                                  stayLength:
                                    values?.flexible?.stayLength ?? "Weekend",
                                  selectedMonth: month,
                                  nights: values?.flexible?.nights ?? 1,
                                },
                              }))
                            }
                          >
                            {format(month, "MMM yyyy")}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mt-5 flex w-full items-center justify-end">
                    <span
                      className="mr-10 cursor-pointer font-semibold text-tier-gold"
                      onClick={() => {
                        setIsFocused(false);
                        if (values) {
                          if (
                            values.type === "date" &&
                            disableSameDateSelection &&
                            values.date?.startDateStr !== undefined &&
                            values.date?.startDateStr ===
                              values.date?.endDateStr
                          ) {
                            const startDateStr = values.date
                              ?.startDateStr as string;
                            const endDate = addDays(
                              getDateFormat(startDateStr),
                              1
                            );
                            const endDateStr = format(endDate, "yyyy-MM-dd");
                            onChange?.(
                              {
                                type: "date",
                                date: { startDateStr, endDateStr },
                              },
                              true
                            );
                          } else {
                            onChange?.(values, true);
                          }
                        }
                      }}
                    >
                      Done
                    </span>
                  </div>
                </div>
              ) : (
                <DateRange
                  months={monthsToDisplay}
                  direction="horizontal"
                  showDateDisplay={false}
                  showMonthAndYearPickers={false}
                  maxDate={addMonths(new Date(), 12)}
                  ranges={
                    values?.date?.startDateStr && values?.date?.endDateStr
                      ? [
                          {
                            startDate: getDateFormat(values.date.startDateStr),
                            endDate: getDateFormat(values.date.endDateStr),
                            key: "selection",
                          },
                        ]
                      : [
                          {
                            startDate: new Date(),
                            endDate: new Date(),
                            key: "selection",
                          },
                        ]
                  }
                  onRangeFocusChange={(focusedInput) => {
                    if (focusedInput[0] === 0 && focusedInput[1] === 0) {
                      setIsFocused(false);
                    }
                  }}
                  disabledDay={(date) => {
                    if (date < sub(new Date(), { days: 1 })) return true;
                    if (minDate && date < minDate) return true;
                    return false;
                  }}
                  className={cn(
                    "w-full rounded-lg border border-border bg-popover",
                    !isWhiteBg && "shadow-lg"
                  )}
                  classNames={
                    isWhiteBg
                      ? {
                          dateRangeWrapper: "dateRangeWrapperWhiteBg",
                          dayToday: "dayTodayWhiteBg",
                          inRange: "inRangeDatesWhiteBg",
                          endEdge: "inRangeDatesWhiteBg",
                          startEdge: "inRangeDatesWhiteBg",
                          dayInPreview: "inRangePreviewWhiteBg",
                          dayStartPreview: "inRangePreviewWhiteBg",
                          dayEndPreview: "inRangePreviewWhiteBg",
                          monthsHorizontal: "monthsHorizontal",
                          dayNumber: "dayNumberWhiteBg",
                          dayPassive: "dayPassive",
                        }
                      : {
                          dateRangeWrapper: "dateRangeWrapper",
                          dayNumber: "dayNumber",
                          dayToday: "dayToday",
                          monthAndYearPickers: "monthAndYearPickers",
                          inRange: "inRangeDates",
                          endEdge: "inRangeDates",
                          startEdge: "inRangeDates",
                          dayInPreview: "inRangePreview",
                          dayStartPreview: "inRangePreview",
                          dayEndPreview: "inRangePreview",
                          monthsHorizontal: "monthsHorizontal",
                          dayPassive: "dayPassive",
                          dayDisabled: "dayDisabled",
                          monthAndYearWrapper: "monthAndYearWrapper",
                        }
                  }
                  preventSnapRefocus={true}
                  onChange={onChangeDates}
                />
              )}
              {renderFooter?.()}
            </div>
          </div>
        )}
      </div>
    );
  }
);

DateRangeWithFlexible.displayName = "DateRangeWithFlexible";

export const getFlexibleDateLabel = (
  values: DateRangeWithFlexibleData,
  short = false
) => {
  if (values.type === "date") {
    if (!values.date?.startDateStr || !values.date?.endDateStr) {
      return "";
    }
    return `${format(getDateFormat(values.date?.startDateStr) ?? new Date(), short ? "MMM d" : "MMMM d")} - ${values.date?.endDateStr ? format(getDateFormat(values.date?.endDateStr), short ? "MMM d" : "MMMM d") : ""}`;
  }
  return `${values.flexible?.stayLength === "Nights" ? `${values.flexible.nights} Night${values.flexible.nights > 1 ? "s" : ""}` : values.flexible?.stayLength} starting in ${format(values.flexible?.selectedMonth ?? new Date(), short ? "MMM" : "MMMM")}`;
};

export default DateRangeWithFlexible;
